import * as CL from '@design-system/component-library';
import { DetailsWithCard } from '../DetailswithCard/DetailsWithCard.js';
import { Link, generatePath, useNavigate } from 'react-router-dom';
import { Status } from '../Status/Status.js';
import { StatusColor } from '../../common/enums.js';
import { Table } from '../Table/Table.js';
import {
  addDeviceMsg,
  contractEndsMsg,
  contractPeriodEndedMsg,
  contractPeriodEndingMsg,
  contractPeriodMsg,
  deviceShopTransitionMsg,
  deviceUserMsg,
  devicesMsg,
  employeeAddDeviceInstructionMsg,
  employeeDeviceInstructionCardContentMsg,
  extensionMsg,
  imeiSlashSerialNumberMsg,
  monthMsg,
  noDevicesMsg,
  omaElisaForEmployeeMsg,
  t,
} from '../../common/i18n/index.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import { formatTimestampToUTCDDMMYYYY } from '../../common/utils/dateUtils.js';
import {
  isEndDateExpired,
  isEndDateExpiring,
  isEppDeviceSubscription,
} from '../EmployeeSubscriptionDetails/eppSubscriptionUtils.js';
import { isInExtensionPeriod } from '../../common/utils/subscriptionUtils.js';
import { paths } from '../../common/constants/pathVariables.js';
import { useSelector } from 'react-redux';
import { useTitle } from '../../common/hooks/useTitle.js';
import type { EmployeeSubscription } from '../../common/types/subscription.js';
import type { State } from '../../selfservice/common/store.js';

import './EmployeeDeviceSubscriptions.scss';

const getContractColor = (subscription: EmployeeSubscription) => {
  if (isInExtensionPeriod(subscription)) {
    return StatusColor.YELLOW;
  }
  if (isEndDateExpired(subscription.details?.contractEndDate ?? 0)) {
    return StatusColor.GREY;
  }
  if (isEndDateExpiring(subscription.details?.contractEndDate ?? 0)) {
    return StatusColor.RED;
  }
  return StatusColor.GREEN;
};

const getContractEndDateText = (subscription: EmployeeSubscription) => {
  const endDateText = formatTimestampToUTCDDMMYYYY(subscription.details?.contractEndDate);
  let periodText = subscription.details?.contractTerm
    ? `${subscription.details?.contractTerm} ${t.XXVX(monthMsg)}`
    : '';
  if (isInExtensionPeriod(subscription)) {
    periodText += ` 6 ${t.XXVX(monthMsg)} ${t.QYMV(extensionMsg).toLowerCase()}`;
  } else if (isEndDateExpired(subscription.details?.contractEndDate ?? 0)) {
    periodText += ` ${t.ZJRN(contractPeriodEndedMsg).toLowerCase()}`;
  } else if (isEndDateExpiring(subscription.details?.contractEndDate ?? 0)) {
    periodText += ` ${t.DRSH(contractPeriodEndingMsg).toLowerCase()}`;
  }
  return (
    <>
      <div className={dsClass.FONT_WEIGHT_600}>{endDateText}</div>
      <div>{periodText}</div>
    </>
  );
};

const getDeviceRows = (deviceSubs: EmployeeSubscription[] = []) => {
  return deviceSubs.map(subscription => {
    const effectivePath = `${generatePath(paths.EMPLOYEE_DEVICE, { subscriptionId: subscription.subscriptionId })}`;
    return {
      subscriptionName: (
        <Link to={effectivePath}>
          <div>{subscription.subscriptionName}</div>
        </Link>
      ),
      subscriptionUserFriendlyId: subscription.subscriptionUserFriendlyId,
      deviceUser: subscription.subscriptionContactName ?? subscription.subscriptionPurposeOfUse ?? '',
      contractEndDate: isEppDeviceSubscription(subscription) ? (
        <Status
          color={getContractColor(subscription)}
          inTable={true}
          textAfterBadge={getContractEndDateText(subscription)}
        />
      ) : (
        ''
      ),
    };
  });
};

const EmployeeDeviceInstructionsCard = () => (
  <>
    <h3>{t.NZRG(contractPeriodMsg)}</h3>
    <p>{t.E6LF(employeeDeviceInstructionCardContentMsg)}</p>
  </>
);

const AddDeviceSection = () => {
  const navigate = useNavigate();
  return (
    <>
      <h3>{t.SQW6(addDeviceMsg)}</h3>
      <p>{t.Z6E1(employeeAddDeviceInstructionMsg)}</p>
      <CL.LinkBlock
        title={t.SQW6(addDeviceMsg)}
        icon={<CL.Icon icon="devices" />}
        onClick={() => navigate(paths.EMPLOYEE_DEVICE_ADD)}
      />
    </>
  );
};

export const EmployeeDeviceSubscriptions = () => {
  const navigate = useNavigate();
  const deviceSubs = useSelector((state: State) => state.user?.authenticated?.subscriptions?.device, deepEqual);

  useTitle([t.H6JV(omaElisaForEmployeeMsg), t.JJTM(devicesMsg)]);

  const columns = [
    {
      key: 'subscriptionName',
      label: t.JJTM(devicesMsg),
    },
    { key: 'subscriptionUserFriendlyId', label: t.OE45(imeiSlashSerialNumberMsg) },
    { key: 'deviceUser', label: t.RCQ9(deviceUserMsg) },
    { key: 'contractEndDate', label: t.UTIR(contractEndsMsg) },
  ];

  return (
    <div className="of-employee-subscriptions">
      <div className="of-employee-subscriptions__buttons">
        <CL.Button onClick={() => navigate(paths.EMPLOYEE_DEVICE_STORE)} size="l">
          {t.HSVK(deviceShopTransitionMsg)}
        </CL.Button>
      </div>
      <DetailsWithCard cardContent={<EmployeeDeviceInstructionsCard />} cardPicto="information">
        <Table
          columns={columns}
          noItemsText={t.LRT3(noDevicesMsg)}
          rows={getDeviceRows(deviceSubs)}
          tableType="bordered"
          noMarginTop
        />
        <AddDeviceSection />
      </DetailsWithCard>
    </div>
  );
};
